
import {catchError} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ExceptionService, PaginatedList } from '../../xform-compat';
import { Headline, RmsSearch } from '../../shared/models';


/**
 * Data service to interact with the Headlines server requests
 */
@Injectable({
  providedIn: 'root'
})
export class HeadlinesService implements RmsSearch<Headline> {

  constructor(
    private http: HttpClient,
    private exceptionService: ExceptionService
  ) {
  }

  /**
   * Gets a single headline
   * @param {number} headlineId: Headline id to get
   * @returns {Observable<Headline>}: Single Headline from the response
   */
  public getSingleHeadline(headlineId: number): Observable<Headline> {
    return this.http
      .get(`/api/rms/headlines/${headlineId}`).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Implements RmsSearch - Searches the list of headlines based on a search term
   * @param {string} term: term for the query string to search for
   * @param params: extra params like pagination settings
   * @returns {Observable<PaginatedList<Headline>>}: Response with the matched list based on the search term
   */
  search(term: string, params: any): Observable<PaginatedList<Headline>> {
    return this.http
      .get(`/api/rms/companies/${params.companyId}/headlines?q=${term}`).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Retrieves the set of distinct headline categories currently present in the
   * database. Categories are assigned by the LLM classifier, so this reflects
   * the live taxonomy rather than a fixed lookup table.
   * @returns {Observable<Array<string>>}: Distinct category names
   */
  public getCategories(): Observable<Array<string>> {
    return this.http
      .get<Array<string>>('/api/rms/headlines/categories').pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Retrieves the publish date of the oldest headline that carries a classification —
   * effectively the date the classifier started running. Headlines older than this were never
   * read by it, so a category filter can explain itself rather than silently hiding them.
   *
   * Resolves to null when nothing has been classified yet: the endpoint answers 204, which
   * HttpClient surfaces as a null body.
   * @returns {Observable<string | null>}: ISO date-time, or null
   */
  public getClassificationStartDate(): Observable<string | null> {
    return this.http
      .get<string | null>('/api/headlines/classificationstartdate').pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Receives the params of the filters and request the list of headlines
   * @param obj Request body
   * @param pageSize Amount of elements in the page
   * @param pageNumber 0 based page number to request
   */
  public getHeadlinesList(obj: any, pageSize: number, pageNumber?: number): Observable<PaginatedList<Headline>> {
    let pageNumberQuery = '';
    if (pageNumber !== undefined) {
      pageNumberQuery = '&page=' + pageNumber;
    }
    // extraField=companyDomains asks the backend to attach each company's domains to the
    // headline's company objects so the client-side domain filter (FD-09) can match on them.
    return this.http
      .post(`/api/rms/headlines/search?pagesize=${pageSize}${pageNumberQuery}&extraField=companyDomains`, obj).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Counts the headlines matching the given filter without retrieving any of them.
   * Takes the same body as {@link getHeadlinesList}, so the count agrees with the
   * total of the equivalent search.
   * @param obj Request body with the search criteria
   * @returns {Observable<number>}: Amount of headlines matching the criteria
   */
  public countHeadlines(obj: any): Observable<number> {
    return this.http
      .post<number>('/api/rms/headlines/search/count', obj).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Request for headlines notifications follow up
   * @param body
   * @returns {Observable<any>}
   */
  public sendFollowUp(body: any): Observable<any> {
    return this.http.post('/api/rms/headlines/share', body).pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Downloads an exported headlines list based on date range
   * @param obj Json body
   * @returns {Observable<Blob>}
   */
  public exportHeadlines(obj: any): Observable<Blob> {
    const headers = new HttpHeaders({ 'Accept': 'application/octet-stream' });
    return this.http.post(`/api/rms/headlines/export`, obj, { headers: headers, responseType: 'blob' }).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }
}
